import { getLiberiaApiBaseUrl } from '@/utils/liberiaApi';

// Per-country paid portals. Vendor Portal writes country_module_settings;
// country Admin can only assign roles that are enabled for that country.

export interface ModuleConfig {
  lawEnforcement: boolean;
  ems: boolean;
  business: boolean;
  partner: boolean;
  surveyor: boolean;
  addressGeneration: boolean;
  pricing: boolean;
  fireDepartment: boolean;
  disasterManagement: boolean;
  utilityManagement: boolean;
  roadInfrastructure: boolean;
  wasteManagement: boolean;
  taxRevenue: boolean;
  censusStatistics: boolean;
  permitLicensing: boolean;
  propertyManagement: boolean;
  realEstate: boolean;
  deliveryLogistics: boolean;
  tourismHospitality: boolean;
  educationServices: boolean;
  healthServices: boolean;
  socialServices: boolean;
  businessRegistration: boolean;
  taxpayer: boolean;
  communityMapping: boolean;
}

export interface VendorModuleCatalogItem {
  settingKey: string;
  configKey: keyof ModuleConfig;
  label: string;
  description: string;
}

/** Keep settingKey values in sync with vendor-module-service.js VENDOR_MODULE_KEYS. */
export const VENDOR_MODULE_CATALOG: VendorModuleCatalogItem[] = [
  { settingKey: 'module_partner', configKey: 'partner', label: 'Partner Portal', description: 'Partner organizations and field mapping' },
  { settingKey: 'module_surveyor', configKey: 'surveyor', label: 'Surveyor Portal', description: 'Land surveying and mapping' },
  { settingKey: 'module_law_enforcement', configKey: 'lawEnforcement', label: 'Law Enforcement', description: 'Dispatch and law enforcement management' },
  { settingKey: 'module_ems', configKey: 'ems', label: 'EMS', description: 'Emergency medical services' },
  { settingKey: 'module_business', configKey: 'business', label: 'Business Portal', description: 'QLS operating costs and collections' },
  { settingKey: 'module_fire_department', configKey: 'fireDepartment', label: 'Fire Department', description: 'Fire operations and management' },
  { settingKey: 'module_disaster_management', configKey: 'disasterManagement', label: 'Disaster Management', description: 'Emergency and disaster coordination' },
  { settingKey: 'module_utility_management', configKey: 'utilityManagement', label: 'Utility Management', description: 'Utility services and infrastructure' },
  { settingKey: 'module_road_infrastructure', configKey: 'roadInfrastructure', label: 'Road Infrastructure', description: 'Road and infrastructure maintenance' },
  { settingKey: 'module_waste_management', configKey: 'wasteManagement', label: 'Waste Management', description: 'Waste collection and services' },
  { settingKey: 'module_tax_revenue', configKey: 'taxRevenue', label: 'Tax & Revenue', description: 'Tax collection and revenue' },
  { settingKey: 'module_census_statistics', configKey: 'censusStatistics', label: 'Census & Statistics', description: 'Census data and statistics' },
  { settingKey: 'module_permit_licensing', configKey: 'permitLicensing', label: 'Permit & Licensing', description: 'Building permits and licenses' },
  { settingKey: 'module_property_management', configKey: 'propertyManagement', label: 'Property Management', description: 'Property records' },
  { settingKey: 'module_real_estate', configKey: 'realEstate', label: 'Real Estate', description: 'Listings and transactions' },
  { settingKey: 'module_delivery_logistics', configKey: 'deliveryLogistics', label: 'QLS Logistics Services', description: 'Last-mile marketplace and tracking' },
  { settingKey: 'module_tourism_hospitality', configKey: 'tourismHospitality', label: 'Tourism & Hospitality', description: 'Attractions and visitor services' },
  { settingKey: 'module_education_services', configKey: 'educationServices', label: 'Education Services', description: 'Schools and education' },
  { settingKey: 'module_health_services', configKey: 'healthServices', label: 'Health Services', description: 'Healthcare facilities' },
  { settingKey: 'module_social_services', configKey: 'socialServices', label: 'Social Services', description: 'Social welfare programs' },
  { settingKey: 'module_taxpayer', configKey: 'taxpayer', label: 'Taxpayer Portal', description: 'Taxpayer bills and payments' },
  { settingKey: 'module_business_registration', configKey: 'businessRegistration', label: 'Business Registration', description: 'Officer filings and public registration' },
  { settingKey: 'module_community_mapping', configKey: 'communityMapping', label: 'Community Mapping', description: 'Citizen-led mapping and validation' },
  { settingKey: 'module_pricing', configKey: 'pricing', label: 'Pricing', description: 'Pricing models and payment management' },
];

let cachedModules: ModuleConfig | null = null;
const cachedModulesByCountry = new Map<string, ModuleConfig>();

function settingEnabled(
  data: Array<{ setting_key?: string; setting_value?: string }>,
  key: string,
  fallback = true
): boolean {
  const row = data.find((setting) => setting.setting_key === key);
  if (!row) {
    return fallback;
  }
  return row.setting_value === 'true';
}

function configFromSettings(data: Array<{ setting_key?: string; setting_value?: string }>): ModuleConfig {
  const base = getDefaultConfig();
  const next = { ...base };
  for (const item of VENDOR_MODULE_CATALOG) {
    next[item.configKey] = settingEnabled(data, item.settingKey, true);
  }
  // Address search stays on unless an explicit global row turns it off.
  next.addressGeneration = settingEnabled(data, 'module_address_generation', true);
  return next;
}

export const fetchModuleConfig = async (countryId?: string | null): Promise<ModuleConfig> => {
  try {
    const apiBaseUrl = getLiberiaApiBaseUrl();
    if (!apiBaseUrl) {
      return getDefaultConfig();
    }

    if (countryId && cachedModulesByCountry.has(countryId)) {
      return cachedModulesByCountry.get(countryId) as ModuleConfig;
    }

    const qs = countryId ? `?country_id=${encodeURIComponent(countryId)}` : '';
    const response = await fetch(`${apiBaseUrl}/api/v1/public/module-settings${qs}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return getDefaultConfig();
    }

    const payload = await response.json();
    const data = Array.isArray(payload?.data) ? payload.data : [];
    const config = data.length === 0 ? getDefaultConfig() : configFromSettings(data);

    if (countryId) {
      cachedModulesByCountry.set(countryId, config);
    } else {
      cachedModules = config;
    }
    return config;
  } catch (error) {
    console.error('Error in fetchModuleConfig:', error);
    return getDefaultConfig();
  }
};

export const getDefaultConfig = (): ModuleConfig => ({
  lawEnforcement: true,
  ems: true,
  business: true,
  partner: true,
  surveyor: true,
  addressGeneration: true,
  pricing: true,
  fireDepartment: true,
  disasterManagement: true,
  utilityManagement: true,
  roadInfrastructure: true,
  wasteManagement: true,
  taxRevenue: true,
  censusStatistics: true,
  permitLicensing: true,
  propertyManagement: true,
  realEstate: true,
  deliveryLogistics: true,
  tourismHospitality: true,
  educationServices: true,
  healthServices: true,
  socialServices: true,
  businessRegistration: true,
  taxpayer: true,
  communityMapping: true,
});

export const getModuleConfig = (): ModuleConfig => {
  return cachedModules || getDefaultConfig();
};

export const clearModuleConfigCache = (countryId?: string | null) => {
  if (countryId) {
    cachedModulesByCountry.delete(countryId);
  } else {
    cachedModules = null;
    cachedModulesByCountry.clear();
  }
};

fetchModuleConfig();

export const isModuleEnabled = (module: keyof ModuleConfig): boolean => {
  const config = getModuleConfig();
  return config[module];
};

export const getEnabledModules = (): string[] => {
  const config = getModuleConfig();
  return Object.entries(config)
    .filter(([_, enabled]) => enabled)
    .map(([module]) => module);
};
